/**
 * Settings' Partners (migration 0096; T3 of PLAN_tax_treatments §4.1 and
 * §4.2): a partnership's partners, the three accounts each is made with, and
 * their shares from a date.
 *
 * Shown only in a partnership. Anyone signed in reads it; whoever manages the
 * business adds a partner and records shares (the API's rule).
 *
 * SHARES ARE RECORDED AS A WHOLE SET FROM A DATE, each column totalling 100%:
 * the database refuses a set that does not, at COMMIT, and refuses a change
 * inside a closed year, whose close used the shares in force. The screen sends
 * percentages to four places as millionths and shows the server's sentence.
 *
 * A PARTNER'S PARTY (0097, round 193's TW-2) is how the partners' capital
 * statement finds a guaranteed payment's partner: the party its entry names.
 * One party is one partner's, which the database holds.
 *
 * Read on its own, as Funds is, so a failure here is said here and leaves the
 * rest of Settings working.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type Party, type PartnersList, type PartnerShareSet } from '../api.js';
import { useCan } from '../auth/AuthProvider.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

/** "37.5" from 375,000 millionths. */
function toPercent(millionths: number): string {
  return String(Number((millionths / 10_000).toFixed(4)));
}

/** 375,000 millionths from "37.5", or null for what is not a percentage to four places. */
function toMillionths(text: string): number | null {
  if (!/^\d{1,3}(\.\d{1,4})?$/.test(text.trim())) return null;
  const value = Math.round(Number(text.trim()) * 10_000);
  return value <= 1_000_000 ? value : null;
}

export function PartnersSection() {
  const manages = useCan()('manage_business');
  const [list, setList] = useState<PartnersList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  // The browser's own day, as a default only: the server judges the date.
  const [effectiveOn, setEffectiveOn] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [shares, setShares] = useState<Record<string, { profit: string; loss: string }>>({});
  const [parties, setParties] = useState<Party[]>([]);

  function load() {
    api
      .listPartners()
      .then((result) => {
        setList(result);
        // The form starts from the latest set, so a change is typed over what stands.
        const latest = result.shareSets[0];
        setShares(
          Object.fromEntries(
            result.partners.map((partner) => {
              const share = latest?.shares.find((each) => each.partnerId === partner.id);
              return [partner.id, { profit: share ? toPercent(share.profitMillionths) : '0', loss: share ? toPercent(share.lossMillionths) : '0' }];
            }),
          ),
        );
      })
      .catch((caught: unknown) => setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the partners.'));
  }
  useEffect(load, []);
  useEffect(() => {
    // The choices for a partner's party; only whoever may change it needs them.
    if (!manages) return;
    api
      .listParties()
      .then((result) => setParties(result.parties))
      .catch(() => setParties([]));
  }, [manages]);

  async function chooseParty(partnerId: string, partyId: string | null) {
    setBusy(true);
    setError(null);
    try {
      const { partner } = await api.setPartnerParty(partnerId, partyId);
      setNotice(partner.partyName === null ? `${partner.name} names no party` : `${partner.name}'s party is ${partner.partyName}`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the partner's party.");
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const { partner } = await api.addPartner(name.trim());
      setName('');
      setNotice(`Added ${partner.name}, with ${partner.capital.code} to ${partner.draws.code}`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add the partner.');
    } finally {
      setBusy(false);
    }
  }

  async function record() {
    if (list === null) return;
    const set: PartnerShareSet = { effectiveOn, shares: [] };
    for (const partner of list.partners) {
      const typed = shares[partner.id] ?? { profit: '0', loss: '0' };
      const profit = toMillionths(typed.profit);
      const loss = toMillionths(typed.loss);
      if (profit === null || loss === null) {
        setError(`${partner.name}'s shares must each be a percentage from 0 to 100, to four places.`);
        return;
      }
      if (profit > 0 || loss > 0) set.shares.push({ partnerId: partner.id, profitMillionths: profit, lossMillionths: loss });
    }
    setBusy(true);
    setError(null);
    try {
      setList(await api.recordPartnerShares(set));
      setNotice(`Recorded the shares from ${effectiveOn}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not record the shares.');
    } finally {
      setBusy(false);
    }
  }

  const latest = list?.shareSets[0];
  const shareOf = (partnerId: string) => latest?.shares.find((share) => share.partnerId === partnerId);

  return (
    <section className="settings-section" aria-labelledby="settings-partners">
      <h2 id="settings-partners">Partners</h2>
      <p className="muted">
        The partners, the accounts each holds, and how the year&apos;s result is shared between them.{' '}
        <InfoNote label="How Partners Work">
          Each partner is made with three accounts: Capital, where the year-end close puts their share of the result,
          Contributions, for what they put in, and Draws, for what they take out. Shares are recorded as a whole set from a
          date, profit and loss each totalling 100%, and the close splits the year by the set in force on its last day. A
          change of shares inside a year leaves that year for your CPA to split, so the close refuses it. A set inside a
          closed year is fixed. A partner&apos;s party is the one a guaranteed payment&apos;s entry names, which puts the
          payment in that partner&apos;s row on the partners&apos; capital statement.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {list === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : list.partners.length === 0 ? (
        <p className="muted">No partners yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Accounts</th>
                <th>Party</th>
                <th className="num">Profit Share</th>
                <th className="num">Loss Share</th>
              </tr>
            </thead>
            <tbody>
              {list.partners.map((partner) => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td>
                    {partner.capital.code}, {partner.contributions.code}, {partner.draws.code}
                  </td>
                  <td>
                    {manages ? (
                      <select
                        aria-label={`${partner.name}'s Party`}
                        value={partner.partyId ?? ''}
                        disabled={busy}
                        onChange={(event) => {
                          const chosen = event.target.value;
                          void chooseParty(partner.id, chosen === '' ? null : chosen);
                        }}
                      >
                        <option value="">None</option>
                        {/* The partner's own party stays a choice even when the list has not arrived. */}
                        {partner.partyId !== null && !parties.some((party) => party.id === partner.partyId) && (
                          <option value={partner.partyId}>{partner.partyName}</option>
                        )}
                        {parties.map((party) => (
                          <option key={party.id} value={party.id}>
                            {party.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      (partner.partyName ?? '—')
                    )}
                  </td>
                  <td className="num">{shareOf(partner.id) ? `${toPercent(shareOf(partner.id)!.profitMillionths)}%` : '—'}</td>
                  <td className="num">{shareOf(partner.id) ? `${toPercent(shareOf(partner.id)!.lossMillionths)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {latest && <p className="muted">Shares in force from {latest.effectiveOn}.</p>}
        </div>
      )}

      {manages && list !== null && (
        <div className="new-account" role="group" aria-label="Add A Partner">
          <div className="field-row">
            <label className="field">
              <span>Partner&apos;s Name</span>
              <input value={name} maxLength={100} onChange={(event) => setName(event.target.value)} />
            </label>
            <button type="button" disabled={busy} onClick={() => void add()}>
              Add Partner
            </button>
          </div>
        </div>
      )}

      {manages && list !== null && list.partners.length > 0 && (
        <div className="new-account" role="group" aria-label="Record Shares">
          <label className="field field-medium">
            <span>Shares From</span>
            <input type="date" value={effectiveOn} onChange={(event) => setEffectiveOn(event.target.value)} />
          </label>
          {list.partners.map((partner) => (
            <div className="field-row" key={partner.id}>
              <label className="field field-medium">
                <span>{partner.name}, Profit %</span>
                <input
                  inputMode="decimal"
                  value={shares[partner.id]?.profit ?? '0'}
                  onChange={(event) => {
                    const profit = event.target.value;
                    setShares((now) => ({ ...now, [partner.id]: { loss: now[partner.id]?.loss ?? '0', profit } }));
                  }}
                />
              </label>
              <label className="field field-medium">
                <span>{partner.name}, Loss %</span>
                <input
                  inputMode="decimal"
                  value={shares[partner.id]?.loss ?? '0'}
                  onChange={(event) => {
                    const loss = event.target.value;
                    setShares((now) => ({ ...now, [partner.id]: { profit: now[partner.id]?.profit ?? '0', loss } }));
                  }}
                />
              </label>
            </div>
          ))}
          <button type="button" disabled={busy} onClick={() => void record()}>
            Record Shares
          </button>
        </div>
      )}
    </section>
  );
}
